import { useLingui } from '@lingui/react/macro';

import type { TaskType } from '@/react-query/useGetTasks';
import { useGetCountry } from '@/utils/internationalization/useGetCountry';

import type {
  PreferenceUpdateBody,
  PreferencesResponse,
} from '../../../@types/preferences';

import { PracticePreferencesRadio } from './PracticePreferencesRadio';
import { PracticePreferencesRadioGroup } from './PracticePreferencesRadioGroup';

export const PracticePreferencesSwitch = (props: {
  selectedTask: TaskType;
  preferences: PreferencesResponse;
  preferencesUpdate: (update: PreferenceUpdateBody) => void;
}) => {
  const { selectedTask, preferences, preferencesUpdate } = props;
  const isCanadianPractice = useGetCountry() === 'CA';
  const { t } = useLingui();

  // Canadian-specific constants
  const treatmentTerm = isCanadianPractice
    ? t`cleaning`
    : t`prophylaxis treatment`;
  const taskName = isCanadianPractice ? t`Cleaning` : t`Prophy`;

  switch (selectedTask) {
    case 'BW_DUE':
      return (
        <PracticePreferencesRadioGroup
          preferences={preferences}
          preferencesUpdate={preferencesUpdate}
          preferenceKey="taskBitewings"
          switchLabel={t`Display a checklist task for patients that may need a bitewing radiograph series.`}
          label={t`Frequency associated with the "Bitewings" task`}
          defaultValue={12}
        >
          <PracticePreferencesRadio value={6} label={t`Every 6 months`} />
          <PracticePreferencesRadio value={12} label={t`Every 12 months`} />
          <PracticePreferencesRadio value={18} label={t`Every 18 months`} />
        </PracticePreferencesRadioGroup>
      );

    case 'FLUORIDE_DUE':
      return (
        <PracticePreferencesRadioGroup
          preferences={preferences}
          preferencesUpdate={preferencesUpdate}
          preferenceKey="taskFluoride"
          switchLabel={t`Display a checklist task for patients that may need a fluoride task.`}
          label={t`Frequency associated with the "Fluoride" task`}
          defaultValue={6}
        >
          <PracticePreferencesRadio value={3} label={t`Every 3 months`} />
          <PracticePreferencesRadio value={6} label={t`Every 6 months`} />
          <PracticePreferencesRadio value={12} label={t`Every 12 months`} />
        </PracticePreferencesRadioGroup>
      );

    case 'PERIO_CHART_DUE':
      return (
        <PracticePreferencesRadioGroup
          preferences={preferences}
          preferencesUpdate={preferencesUpdate}
          preferenceKey="taskPerioChart"
          switchLabel={t`Display a checklist task for patients that may need an updated perio chart.`}
          label={t`Frequency associated with the "Perio Chart" task`}
          defaultValue={12}
        >
          <PracticePreferencesRadio value={3} label={t`Every 3 months`} />
          <PracticePreferencesRadio value={6} label={t`Every 6 months`} />
          <PracticePreferencesRadio value={12} label={t`Every 12 months`} />
        </PracticePreferencesRadioGroup>
      );

    case 'PROPHY_DUE':
      return (
        <PracticePreferencesRadioGroup
          preferences={preferences}
          preferencesUpdate={preferencesUpdate}
          preferenceKey="taskProphy"
          switchLabel={t`Display a checklist task for patients that may need a ${treatmentTerm}.`}
          label={t`Frequency associated with the "${taskName}" task`}
          defaultValue={6}
        >
          <PracticePreferencesRadio value={3} label={t`Every 3 months`} />
          <PracticePreferencesRadio value={6} label={t`Every 6 months`} />
          {isCanadianPractice && (
            <PracticePreferencesRadio value={9} label={t`Every 9 months`} />
          )}
          <PracticePreferencesRadio value={12} label={t`Every 12 months`} />
        </PracticePreferencesRadioGroup>
      );

    case 'PERIO_MAINTENANCE_DUE':
      return (
        <PracticePreferencesRadioGroup
          preferences={preferences}
          preferencesUpdate={preferencesUpdate}
          preferenceKey="taskPerioMaintenance"
          switchLabel={t`Display a checklist task for patients that may need a Perio Maintenance treatment.`}
          label={t`Frequency associated with the "Perio Maintenance" task`}
          defaultValue={4}
        >
          <PracticePreferencesRadio value={3} label={t`Every 3 months`} />
          <PracticePreferencesRadio value={4} label={t`Every 4 months`} />
          <PracticePreferencesRadio value={6} label={t`Every 6 months`} />
        </PracticePreferencesRadioGroup>
      );

    case 'SCAN_OPPORTUNITY':
      return (
        <PracticePreferencesRadioGroup
          preferences={preferences}
          preferencesUpdate={preferencesUpdate}
          preferenceKey="taskScanOpportunity"
          switchLabel={t`Display a checklist task for patients that have a 3D Scan opportunity.`}
          label={t`Frequency associated with the "3D Scan Opportunity" task`}
          defaultValue={6}
        >
          <PracticePreferencesRadio value={3} label={t`Every 3 months`} />
          <PracticePreferencesRadio value={6} label={t`Every 6 months`} />
          <PracticePreferencesRadio value={12} label={t`Every 12 months`} />
        </PracticePreferencesRadioGroup>
      );

    default:
      return <></>;
  }
};
